import { useMemo } from 'react';
import useMileagePackageCopy, {
  UseMileagePackageCopyProps,
} from './use-mileage-package-copy';
import { PlanRadioButtonCopy } from '../components/pdp/plan-radio-group/plan-radio-button';

const mostPopularCopy = '30%+ savings on additional miles';

interface UseMileagePlanCopyProps extends UseMileagePackageCopyProps {
  disableMileagePlanSelection?: boolean;
  inSwapFlow?: boolean;
}

interface UseMileagePlanCopyResponse {
  mileagePlansCopy: PlanRadioButtonCopy[];
  zeroMilePlanId: number | undefined;
}

export default function useMileagePlanCopy({
  plans: mileagePlans,
  mostPopularPlanMileageAmount,
  splitPayment,
  disableMileagePlanSelection,
  inSwapFlow,
}: UseMileagePlanCopyProps): UseMileagePlanCopyResponse {
  const composedMileagePlans = useMileagePackageCopy({
    plans: mileagePlans,
    mostPopularPlanMileageAmount,
    splitPayment,
  });

  const mileagePlansCopy: PlanRadioButtonCopy[] = useMemo(() => {
    if (!composedMileagePlans) return [];

    return composedMileagePlans.map((mileagePlan) => ({
      id: String(mileagePlan.id),
      name: mileagePlan.planName,
      pricePerMonthCopy: mileagePlan.monthlyCostCopy,
      details: [mileagePlan.mileageIncludedCopy].concat(
        mileagePlan.isMostPopular ? [mostPopularCopy] : []
      ),
      tag: mileagePlan.isMostPopular ? 'best-value' : undefined,
      disabled:
        mileagePlan.isZeroMilePlan && splitPayment ? true : disableMileagePlanSelection,
      isSwap: inSwapFlow,
    }));
  }, [composedMileagePlans, disableMileagePlanSelection, inSwapFlow, splitPayment]);

  const zeroMilePlanId = useMemo(
    () => composedMileagePlans?.find((plan) => plan.isZeroMilePlan)?.id,
    [composedMileagePlans]
  );

  return {
    mileagePlansCopy,
    zeroMilePlanId,
  };
}
